import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActionBadge, formatAnalyzedAt } from './components/DecisionWidgets';
import { useDecisionPortfolio } from './DecisionPortfolioContext';

function formatMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  if (Math.abs(n) >= 10000) return `${(n / 10000).toFixed(2)}万`;
  return n.toFixed(2);
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const {
    summaries,
    results,
    sync,
    disclosureCoverage,
    loading,
    analyzing,
    error,
    analyzedOnce,
    ensureLoaded,
    analyzeAll
  } = useDecisionPortfolio();

  useEffect(() => {
    void ensureLoaded();
  }, [ensureLoaded]);

  const stats = useMemo(() => {
    const valid = results.filter((r) => r.ok && r.score != null);
    const attention = valid.filter((r) => ['reduce', 'sell', 'hold_reduce'].includes(r.action));
    const avg = valid.length
      ? Math.round(valid.reduce((a, b) => a + b.score, 0) / valid.length)
      : null;
    return {
      holdingCount: summaries.length,
      analyzedCount: valid.length,
      attention: attention.length,
      avg
    };
  }, [results, summaries]);

  const sorted = useMemo(
    () => [...results].filter((r) => r.ok).sort((a, b) => (a.score ?? 0) - (b.score ?? 0)),
    [results]
  );

  const openProduct = useCallback(
    (productId, agentOrg) => {
      if (!productId) return;
      const params = new URLSearchParams({ id: productId });
      if (agentOrg) params.set('agent', agentOrg);
      navigate(`/product?${params.toString()}`);
    },
    [navigate]
  );

  return (
    <div>
      <h1 className="decision-page-title">决策总览</h1>
      <p className="decision-page-sub">先展示持仓，需要时再批量分析（切 Tab 不会重复拉数）</p>

      {loading ? <p className="decision-muted">正在同步持仓…</p> : null}
      {analyzing ? <p className="decision-muted">正在分析组合（可稍候，勿重复点击）…</p> : null}
      {error ? <p className="decision-error">{error}</p> : null}
      {sync?.synced ? (
        <p className="decision-muted">
          持仓已从{sync.authHost === 'overseas' ? '野草云' : '腾讯'}会员库同步
          {sync.holdingCount != null ? `（${sync.holdingCount} 条）` : ''}
        </p>
      ) : null}

      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="decision-btn"
          onClick={() => void analyzeAll()}
          disabled={loading || analyzing || !summaries.length}
        >
          {analyzing ? '分析中…' : analyzedOnce ? '重新分析组合' : '分析组合'}
        </button>
        <button
          type="button"
          className="decision-btn ghost"
          onClick={() => void ensureLoaded({ force: true })}
          disabled={loading || analyzing}
        >
          刷新持仓
        </button>
      </div>

      <div className="decision-grid decision-grid-4">
        <div className="decision-card">
          <h3>持仓产品</h3>
          <div className="decision-kpi-value">{stats.holdingCount}</div>
          <div className="decision-kpi-label">已同步</div>
        </div>
        <div className="decision-card">
          <h3>需关注</h3>
          <div className="decision-kpi-value">{analyzedOnce ? stats.attention : '—'}</div>
          <div className="decision-kpi-label">减仓/卖出信号</div>
        </div>
        <div className="decision-card">
          <h3>组合均分</h3>
          <div className="decision-kpi-value">{analyzedOnce ? (stats.avg ?? '—') : '—'}</div>
          <div className="decision-kpi-label">0–100</div>
        </div>
        <div className="decision-card">
          <h3>文档洞察</h3>
          <div className="decision-kpi-value">{disclosureCoverage?.prospectus_count ?? 0}</div>
          <div className="decision-kpi-label">
            已发布说明书 · 运作报告 {disclosureCoverage?.report_count ?? 0} 份
          </div>
        </div>
      </div>

      <div className="decision-card" style={{ marginTop: 16 }}>
        <h3>持仓列表</h3>
        {!summaries.length && !loading ? (
          <p className="decision-muted">
            暂无持仓。请确认管理台用户名与会员超级 VIP 一致，且已开启云同步。
          </p>
        ) : (
          <>
            <p className="table-hint">点击行进入单产品分析；批量结论请先点「分析组合」</p>
            <table className="decision-table decision-table-interactive">
              <thead>
                <tr>
                  <th>产品</th>
                  <th>机构</th>
                  <th>占比</th>
                  <th>市值估算</th>
                  <th>综合分</th>
                  <th>建议</th>
                  <th>分析时间</th>
                </tr>
              </thead>
              <tbody>
                {summaries.map((row) => {
                  const analyzed = results.find((r) => String(r.product_id) === String(row.product_id));
                  return (
                    <tr
                      key={row.product_id}
                      onClick={() => openProduct(row.product_id, row.agent_org)}
                      title="点击查看产品分析"
                    >
                      <td>{row.product_name}</td>
                      <td>{row.agent_org || '—'}</td>
                      <td>{row.weight_pct != null ? `${row.weight_pct}%` : '—'}</td>
                      <td>{formatMoney(row.market_value)}</td>
                      <td>{analyzed?.score ?? '—'}</td>
                      <td>
                        {analyzed ? (
                          <ActionBadge action={analyzed.action} label={analyzed.label} />
                        ) : (
                          <span className="decision-muted">未分析</span>
                        )}
                      </td>
                      <td className="decision-muted">{formatAnalyzedAt(analyzed?.analyzed_at, { stale: analyzed?.stale })}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </div>

      {analyzedOnce && sorted.length ? (
        <div className="decision-card" style={{ marginTop: 16 }}>
          <h3>决策排序（低分优先）</h3>
          <table className="decision-table decision-table-interactive">
            <thead>
              <tr>
                <th>产品</th>
                <th>机构</th>
                <th>综合分</th>
                <th>建议</th>
                <th>分析时间</th>
                <th>摘要</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => (
                <tr
                  key={`sorted-${row.product_id}`}
                  onClick={() => openProduct(row.product_id, row.agent_org)}
                >
                  <td>{row.product_name}</td>
                  <td>{row.agent_org}</td>
                  <td>{row.score ?? '—'}</td>
                  <td><ActionBadge action={row.action} label={row.label} /></td>
                  <td className="decision-muted">{formatAnalyzedAt(row.analyzed_at, { stale: row.stale })}</td>
                  <td>{row.summary}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
